/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useState, useEffect, useContext } from 'react';
import SelectDataPanels from './SelectDataPanels';
import SelectLayers from './SelectLayers';
import useCommands from 'comps/Left2/useCommands';
import useContextState from 'comps/Left2/useContextState';
import { q } from 'ops';
import * as I from 'immutable';
import commands from './commands';
import Context from './Context';

export function useActiveLayerSync(names: string[]) {
  const cmd = useCommands();
  const getLayers = useLayers();

  // Sync selected layers and active layers
  useEffect(() => {
    // Get layers from loadedLayers
    const layers = getLayers(names);
    layers.forEach((layer) => {
      cmd.addActiveLayer(layer);
    });
  }, [names]);
}

function useLayers() {
  const s = useContextState();

  return function getLayers(names: string[]) {
    if (names.length > 0) {
      return I.fromJS(s.layersLoaded.filter((v) => names.includes(v.name)));
    }
    return I.List();
  };
}

export function useDeckSync(names: string[]) {
  const getLayers = useLayers();

  // Sync selected layers and active layers
  useEffect(() => {
    // Get layers from loadedLayers
    const layers = getLayers(names);

    // Get query of all activeLayers
    const deck = layers
      .map((v) => v.get('query'))
      .reduce((s2, v) => s2.mergeDeep(v || I.Map()), I.Map());

    // Update query
    q((v) => v.set('deck', deck.get('deck')));
  }, [names]);
}

function useMapSettingCommands() {
  const ctx = useContext(Context);
  return commands(ctx);
}

function useMapSettingContextState() {
  const ctx = useContext(Context);
  return ctx.state;
}

export default function MapSettings(): Element<string> {
  const [layerNames, setLayers] = useState([]);
  const cmd = useMapSettingCommands();
  const {
    showBoundary,
    showDrawingTool,
    showPanelsControl,
    showLayersControl,
    showShapes,
  } = useMapSettingContextState();

  // When showLayersControl is false, clear all active
  // layerNames
  useEffect(() => {
    if (showLayersControl) return;
    setLayers([]);
  }, [showLayersControl]);

  useActiveLayerSync(layerNames);
  useDeckSync(layerNames);

  return (
    <div
      data-testid="MapSettings"
      className="mb-4"
      css={css(`
        .Select__menu {
          z-index: 999;
        }
      `)}
    >
      <h5 className="font-weight-bold">Map settings</h5>
      <label data-testid="BoundaryOption" className="d-flex align-items-center">
        <span>Show district boundary</span>
        <input
          className="ml-auto"
          type="checkbox"
          checked={showBoundary}
          onChange={(e) => cmd.set('showBoundary', e.target.checked)}
        />
      </label>
      <label
        data-testid="DrawingToolOption"
        className="d-flex align-items-center"
      >
        <span>Show drawing tool</span>
        <input
          type="checkbox"
          className="ml-auto"
          checked={showDrawingTool}
          onChange={(e) => cmd.set('showDrawingTool', e.target.checked)}
        />
      </label>
      <label data-testid="ShapesOption" className="d-flex align-items-center">
        <span>Show shapes</span>
        <input
          type="checkbox"
          className="ml-auto"
          checked={showShapes}
          onChange={(e) => cmd.set('showShapes', e.target.checked)}
        />
      </label>
      <label
        data-testid="DataPanelsOption"
        className="d-flex align-items-center"
      >
        <span>Data panels</span>
        <input
          type="checkbox"
          className="ml-auto"
          data-testid="DataPanelsCheckbox"
          checked={showPanelsControl}
          onChange={(e) => {
            cmd.set('showPanelsControl', e.target.checked);
          }}
        />
      </label>
      {showPanelsControl ? (
        <div data-testid="DataPanelsDropdown" className="mt-2 mb-2">
          <SelectDataPanels onChange={(v) => cmd.set('dataPanels', v)} />
        </div>
      ) : null}
      <label data-testid="LayersOption" className="d-flex align-items-center">
        <span>Layers</span>
        <input
          type="checkbox"
          className="ml-auto"
          data-testid="LayersCheckbox"
          checked={showLayersControl}
          onChange={(e) => cmd.set('showLayersControl', e.target.checked)}
        />
      </label>
      {showLayersControl ? (
        <div data-testid="LayersDropdown" className="mt-2 mb-2">
          <SelectLayers onChange={(vs) => setLayers(vs)} />
        </div>
      ) : null}
    </div>
  );
}
